import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';

const features = [
    {
        title: 'Multi-Store Management',
        desc: 'Manage multiple stores from a single account with seamless switching and centralized control.',
        icon: '🏪',
        category: 'business'
    },
    {
        title: 'Advanced Repair Tracking',
        desc: 'Track active, completed, and pending repairs with real-time status updates and notifications.',
        icon: '🛠️',
        category: 'core'
    },
    {
        title: 'Khata Ledger System',
        desc: 'Professional customer and supplier ledger management with automatic due tracking and payment history.',
        icon: '📋',
        category: 'business'
    },
    {
        title: 'Digital Wallet & Payments',
        desc: 'In-app wallet with coin system, secure payments, and transaction history tracking.',
        icon: '💳',
        category: 'core'
    },
    {
        title: 'Staff Management',
        desc: 'Role-based access control with granular permissions for different business modules.',
        icon: '👥',
        category: 'business'
    },
    {
        title: 'Broadcast Marketing',
        desc: 'Send promotional offers and notifications to all customers with image support.',
        icon: '📢',
        category: 'marketing'
    },
    {
        title: 'Comprehensive Reports',
        desc: 'Revenue, customer, repair, and due reports with detailed analytics and insights.',
        icon: '📊',
        category: 'business'
    },
    {
        title: 'QR Code System',
        desc: 'Generate and manage QR codes for easy customer identification and tracking.',
        icon: '📱',
        category: 'core'
    },
    {
        title: 'Referral Program',
        desc: 'Refer & earn system to grow your customer base and reward loyal customers.',
        icon: '🎁',
        category: 'marketing'
    },
    {
        title: 'Real-time Notifications',
        desc: 'Push notifications and WhatsApp integration for instant communication.',
        icon: '🔔',
        category: 'communication'
    },
    {
        title: 'Cloud Backup & Security',
        desc: 'All data securely backed up in the cloud with enterprise-grade security.',
        icon: '☁️',
        category: 'core'
    },
    {
        title: 'Multi-Platform Access',
        desc: 'Available on Android, iOS, and web for seamless access from any device.',
        icon: '🌐',
        category: 'core'
    },
];

const howItWorks = [
    'Create Store',
    'Add Repairs',
    'Track Progress',
    'Manage Payments',
    'Generate Reports',
];

const businessBenefits = [
    'Multi-Store Management',
    'Advanced Khata System',
    'Staff & Permission Control',
    'Comprehensive Reports',
    'Broadcast Marketing',
    'Digital Wallet & Payments',
    'QR Code Management',
    'Referral Program',
    'Real-time Notifications',
    'Cloud Security',
    'Multi-Platform Access',
    '24/7 Support',
];

const customerBenefits = [
    'Real-time Repair Tracking',
    'Instant Notifications',
    'Digital Receipts',
    'Secure Payments',
    'Exclusive Offers',
    'Referral Rewards',
    'Multi-Platform Access',
    'Professional Service',
];

const testimonials = [
    {
        quote:
            'Fixisy transformed our repair business with multi-store management and the khata system. Tracking jobs and payments is now effortless!',
        name: 'Ashutosh Gaurav',
        role: 'Business Owner',
        rating: 5
    },
    {
        quote:
            'The staff management and permission system is excellent. I can control exactly what each employee can access.',
        name: 'Ruby Kumari',
        role: 'Store Manager',
        rating: 5
    },
    {
        quote:
            'I always know the status of my repairs and love the digital receipts. The referral program is a great bonus!',
        name: 'Ankit Aman',
        role: 'Customer',
        rating: 5
    },
];

const faqs = [
    {
        q: 'Is Fixisy free to use?',
        a: 'Fixisy offers a free tier for all features. Premium features including multi-store management, advanced reports, and broadcast marketing are available mostly free, Some cost some coins that can be purchased in app.',
    },
    {
        q: 'How does multi-store management work?',
        a: 'Business owners can create and manage multiple stores from a single account. Each store has its own data, staff, and settings while maintaining centralized control.',
    },
    {
        q: 'What is the Khata system?',
        a: 'The Khata system is a professional ledger management feature that automatically tracks customer and supplier dues, payments, and transaction history.',
    },
    {
        q: 'How do I get the app?',
        a: 'Getting started is easy! Download Fixisy from Google Play or the App Store, or sign up instantly for web access to manage your repairs from any device.',
    },
    {
        q: 'Is my data secure?',
        a: 'Absolutely. We take your privacy and security seriously. All data is encrypted and backed up securely in the cloud with enterprise-grade security practices.',
    },
    {
        q: 'Can I contact support?',
        a: 'Absolutely! Our support team is here to help you 24/7. Reach out anytime at support@fixisy.com or use the in-app support feature for prompt assistance.',
    },
];

const stats = [
    { number: '10,000+', label: 'Active Users' },
    { number: '50,000+', label: 'Repairs Tracked' },
    { number: '99.9%', label: 'Uptime' },
    { number: '24/7', label: 'Support' },
];

const plans = [
    {
        name: 'Free',
        price: '₹0',
        period: '/month',
        features: [
            'Basic repair tracking',
            'Customer management',
            'Simple reports',
            'Email support',
            'Mobile app access'
        ],
        popular: false
    },
    {
        name: 'Professional',
        price: '₹999',
        period: '/month',
        features: [
            'Multi-store management',
            'Advanced khata system',
            'Staff management',
            'Comprehensive reports',
            'Broadcast marketing',
            'QR code system',
            'Priority support',
            'All features included'
        ],
        popular: true
    },
    {
        name: 'Enterprise',
        price: 'Custom',
        period: '',
        features: [
            'Unlimited stores',
            'Custom integrations',
            'Dedicated support',
            'Advanced analytics',
            'White-label options',
            'API access',
            'Custom training',
            'SLA guarantee'
        ],
        popular: false
    }
];

const LandingPage: React.FC = () => {
    const [appUrls, setAppUrls] = useState<{ android?: string; ios?: string }>({});
    const [loadingUrls, setLoadingUrls] = useState(true);
    const [selectedCategory, setSelectedCategory] = useState<string>('all');

    useEffect(() => {
        const fetchUrls = async () => {
            try {
                const docRef = doc(db, 'Urls', '5gyzDXnn1psSXKVCgktP');
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    setAppUrls({
                        android: docSnap.data().android,
                        ios: docSnap.data().ios,
                    });
                } else {
                    // No document found
                }
            } catch (e) {
                setAppUrls({});
            } finally {
                setLoadingUrls(false);
            }
        };
        fetchUrls();
    }, []);

    return (
        <div className="min-h-screen bg-gray-900 flex flex-col">
            {/* Navigation */}
            <nav className="bg-gray-800 py-4 sticky top-0 z-50">
                <div className="container mx-auto px-4 flex flex-col sm:flex-row justify-between items-center gap-4 sm:gap-0">
                    <div className="flex items-center gap-3">
                        <img src="/images/logo.png" alt="Fixisy Logo" className="h-10 w-10 object-contain" />
                        <Link to="/" className="text-2xl font-bold text-cyan-400">Fixisy</Link>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 items-center">
                        <span className="text-gray-300 text-xs sm:text-sm">Contact: <a href="mailto:support@fixisy.com" className="text-cyan-400 hover:underline">support@fixisy.com</a></span>
                        <Link to="/support" className="px-4 py-2 bg-cyan-700 text-white rounded hover:bg-cyan-800 font-semibold transition-colors w-full sm:w-auto text-center">Support</Link>
                        <Link to="/signin" className="px-4 py-2 bg-cyan-500 text-white rounded hover:bg-cyan-600 font-semibold transition-colors w-full sm:w-auto text-center">Sign In</Link>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="py-16 sm:py-24 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
                <div className="container mx-auto px-4 flex flex-col md:flex-row items-center gap-12">
                    <div className="md:w-1/2 w-full text-center md:text-left">
                        <h1 className="text-4xl md:text-6xl font-bold mb-4 text-white leading-tight">
                            Complete Repair Business Management Platform
                        </h1>
                        <p className="text-xl mb-8 text-gray-300">
                            Multi-store management, advanced khata system, and comprehensive reporting for modern repair businesses.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 items-center justify-center md:justify-start">
                            {/* Play Store Logo */}
                            {appUrls.android ? (
                                <a href={appUrls.android} target="_blank" rel="noopener noreferrer">
                                    <img src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" alt="Get it on Google Play" className="h-12 w-auto" />
                                </a>
                            ) : (
                                <img src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" alt="Get it on Google Play" className="h-12 w-auto opacity-50 cursor-not-allowed" style={{ pointerEvents: 'none' }} />
                            )}
                            {/* App Store Logo */}
                            {appUrls.ios ? (
                                <a href={appUrls.ios} target="_blank" rel="noopener noreferrer">
                                    <img src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg" alt="Download on the App Store" className="h-12 w-auto" />
                                </a>
                            ) : (
                                <img src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg" alt="Download on the App Store" className="h-12 w-auto opacity-50 cursor-not-allowed" style={{ pointerEvents: 'none' }} />
                            )}
                            <Link to="/signup" className="px-6 py-3 bg-cyan-500 text-white rounded font-bold hover:bg-cyan-600 transition-colors text-lg">Sign up for web access</Link>
                        </div>
                    </div>
                    <div className="md:w-1/2 w-full flex justify-center">
                        <img src="/images/logo1.png" alt="App Mockup" className="rounded-xl shadow-2xl w-64 h-64 object-contain bg-gray-800" />
                    </div>
                </div>
            </section>

            {/* Stats Section */}
            <section className="py-12 bg-cyan-700">
                <div className="container mx-auto px-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                        {stats.map((stat, i) => (
                            <div key={i} className="text-center">
                                <div className="text-3xl md:text-4xl font-bold text-white mb-2">{stat.number}</div>
                                <div className="text-cyan-100">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="py-16 bg-gray-800">
                <div className="container mx-auto px-4">
                    <h2 className="text-3xl font-bold text-center mb-4 text-white">Powerful Features</h2>
                    <p className="text-center text-gray-300 mb-12 max-w-2xl mx-auto">
                        Everything you need to manage your repair business efficiently, from multi-store operations to advanced reporting.
                    </p>

                    {/* Feature Categories */}
                    <div className="mb-8 flex flex-wrap justify-center gap-4">
                        <button
                            onClick={() => setSelectedCategory('all')}
                            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${selectedCategory === 'all'
                                ? 'bg-cyan-600 text-white'
                                : 'bg-gray-700 text-gray-300 hover:bg-cyan-600 hover:text-white'
                                }`}
                        >
                            All Features
                        </button>
                        <button
                            onClick={() => setSelectedCategory('business')}
                            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${selectedCategory === 'business'
                                ? 'bg-cyan-600 text-white'
                                : 'bg-gray-700 text-gray-300 hover:bg-cyan-600 hover:text-white'
                                }`}
                        >
                            Business Tools
                        </button>
                        <button
                            onClick={() => setSelectedCategory('core')}
                            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${selectedCategory === 'core'
                                ? 'bg-cyan-600 text-white'
                                : 'bg-gray-700 text-gray-300 hover:bg-cyan-600 hover:text-white'
                                }`}
                        >
                            Core Features
                        </button>
                        <button
                            onClick={() => setSelectedCategory('marketing')}
                            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${selectedCategory === 'marketing'
                                ? 'bg-cyan-600 text-white'
                                : 'bg-gray-700 text-gray-300 hover:bg-cyan-600 hover:text-white'
                                }`}
                        >
                            Marketing
                        </button>
                        <button
                            onClick={() => setSelectedCategory('communication')}
                            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${selectedCategory === 'communication'
                                ? 'bg-cyan-600 text-white'
                                : 'bg-gray-700 text-gray-300 hover:bg-cyan-600 hover:text-white'
                                }`}
                        >
                            Communication
                        </button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
                        {features
                            .filter(f => selectedCategory === 'all' || f.category === selectedCategory)
                            .map((f, i) => (
                                <div key={i} className="p-6 rounded-lg bg-gray-700 flex flex-col items-center text-center shadow-lg hover:shadow-xl transition-all duration-300 hover:bg-gray-600 group">
                                    <div className="text-4xl mb-4 group-hover:scale-110 transition-transform duration-300">{f.icon}</div>
                                    <h3 className="text-xl font-semibold mb-2 text-cyan-400">{f.title}</h3>
                                    <p className="text-gray-300 text-sm">{f.desc}</p>
                                </div>
                            ))}
                    </div>
                </div>
            </section>

            {/* Key Differentiators Section */}
            <section className="py-16 bg-gray-900">
                <div className="container mx-auto px-4">
                    <h2 className="text-3xl font-bold text-center mb-4 text-white">Why Choose Fixisy?</h2>
                    <p className="text-center text-gray-300 mb-12 max-w-2xl mx-auto">
                        Built specifically for repair businesses with features that matter most
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        <div className="bg-gray-800 rounded-lg p-6 text-center">
                            <div className="text-4xl mb-4">🏪</div>
                            <h3 className="text-xl font-semibold text-cyan-400 mb-2">Multi-Store Ready</h3>
                            <p className="text-gray-300">Manage multiple locations from a single account with seamless switching and centralized control.</p>
                        </div>
                        <div className="bg-gray-800 rounded-lg p-6 text-center">
                            <div className="text-4xl mb-4">📋</div>
                            <h3 className="text-xl font-semibold text-cyan-400 mb-2">Professional Khata</h3>
                            <p className="text-gray-300">Advanced ledger system that automatically tracks dues, payments, and customer history.</p>
                        </div>
                        <div className="bg-gray-800 rounded-lg p-6 text-center">
                            <div className="text-4xl mb-4">👥</div>
                            <h3 className="text-xl font-semibold text-cyan-400 mb-2">Granular Permissions</h3>
                            <p className="text-gray-300">Control exactly what each staff member can access with role-based permissions.</p>
                        </div>
                        <div className="bg-gray-800 rounded-lg p-6 text-center">
                            <div className="text-4xl mb-4">📊</div>
                            <h3 className="text-xl font-semibold text-cyan-400 mb-2">Comprehensive Reports</h3>
                            <p className="text-gray-300">Get detailed insights into revenue, customers, repairs, and business performance.</p>
                        </div>
                        <div className="bg-gray-800 rounded-lg p-6 text-center">
                            <div className="text-4xl mb-4">📱</div>
                            <h3 className="text-xl font-semibold text-cyan-400 mb-2">Multi-Platform</h3>
                            <p className="text-gray-300">Access your business from Android, iOS, or web - all data stays in sync.</p>
                        </div>
                        <div className="bg-gray-800 rounded-lg p-6 text-center">
                            <div className="text-4xl mb-4">🔔</div>
                            <h3 className="text-xl font-semibold text-cyan-400 mb-2">Real-time Updates</h3>
                            <p className="text-gray-300">Instant notifications and WhatsApp integration keep everyone informed.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* How It Works Section */}
            <section className="py-16 bg-gray-900">
                <div className="container mx-auto px-4">
                    <h2 className="text-3xl font-bold text-center mb-4 text-white">How It Works</h2>
                    <p className="text-center text-gray-300 mb-12 max-w-2xl mx-auto">
                        Get started with Fixisy in just a few simple steps
                    </p>
                    <div className="relative flex flex-col items-center">
                        {/* Central horizontal line */}
                        <div className="hidden md:block absolute left-0 right-0 top-1/2 h-1 bg-cyan-400 z-0" style={{ transform: 'translateY(-50%)' }}></div>
                        <div className="flex flex-col md:flex-row items-center justify-center w-full">
                            {howItWorks.map((step, i) => (
                                <div key={i} className="relative flex flex-col items-center w-full md:w-1/5 z-10 mb-8 md:mb-0">
                                    {/* Zigzag: label above for odd numbers, below for even */}
                                    {i % 2 === 0 && (
                                        <div className="mb-3 text-base font-semibold text-cyan-300 text-center w-32 md:w-auto" style={{ minHeight: '2.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            {step}
                                        </div>
                                    )}
                                    <div className="flex items-center justify-center w-16 h-16 rounded-full bg-cyan-400 text-white text-2xl font-bold shadow-lg border-4 border-cyan-600">
                                        {i + 1}
                                    </div>
                                    {i % 2 === 1 && (
                                        <div className="mt-3 text-base font-semibold text-cyan-300 text-center w-32 md:w-auto" style={{ minHeight: '2.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            {step}
                                        </div>
                                    )}
                                    {/* Connector line for mobile */}
                                    {i < howItWorks.length - 1 && (
                                        <div className="block md:hidden w-1 h-8 bg-cyan-400 mx-auto"></div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* User Benefits Section */}
            <section className="py-16 bg-gray-800">
                <div className="container mx-auto px-4">
                    <h2 className="text-3xl font-bold text-center mb-4 text-white">Who is Fixisy for?</h2>
                    <p className="text-center text-gray-300 mb-12 max-w-2xl mx-auto">
                        Designed for both repair businesses and their customers
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                        <div className="bg-gray-700 rounded-lg p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
                            <h3 className="text-2xl font-bold text-cyan-400 mb-4">For Businesses & Service Centers</h3>
                            <ul className="list-disc list-inside text-gray-300 space-y-2">
                                {businessBenefits.map((b, i) => (
                                    <li key={i} className="flex items-start">
                                        <span className="text-cyan-400 mr-2">•</span>
                                        {b}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="bg-gray-700 rounded-lg p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
                            <h3 className="text-2xl font-bold text-cyan-400 mb-4">For Customers</h3>
                            <ul className="list-disc list-inside text-gray-300 space-y-2">
                                {customerBenefits.map((c, i) => (
                                    <li key={i} className="flex items-start">
                                        <span className="text-cyan-400 mr-2">•</span>
                                        {c}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* Testimonials Section */}
            <section className="py-16 bg-gray-900">
                <div className="container mx-auto px-4">
                    <h2 className="text-3xl font-bold text-center mb-4 text-white">What Our Users Say</h2>
                    <p className="text-center text-gray-300 mb-12 max-w-2xl mx-auto">
                        Join thousands of satisfied users who trust Fixisy for their repair business
                    </p>
                    <div className="flex flex-col md:flex-row gap-8 justify-center items-stretch">
                        {testimonials.map((t, i) => (
                            <div key={i} className="bg-gray-800 rounded-lg p-8 shadow-lg flex-1 flex flex-col justify-between hover:shadow-xl transition-shadow duration-300">
                                <div>
                                    <div className="flex mb-4">
                                        {[...Array(t.rating)].map((_, star) => (
                                            <span key={star} className="text-yellow-400 text-xl">⭐</span>
                                        ))}
                                    </div>
                                    <p className="text-xl text-cyan-300 mb-4">"{t.quote}"</p>
                                </div>
                                <div className="text-gray-400 font-semibold">- {t.name}, {t.role}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Download/Signup Section */}
            <section className="py-16 bg-gradient-to-r from-cyan-700 to-cyan-600">
                <div className="container mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-8">
                    <div className="flex-1 text-center md:text-left">
                        <h2 className="text-3xl font-bold text-white mb-4">Ready to transform your repair business?</h2>
                        <p className="text-lg text-cyan-100 mb-6">Join thousands of businesses already using Fixisy to streamline their operations!</p>
                        <div className="flex flex-col sm:flex-row gap-4 items-center md:justify-start justify-center">
                            {/* Play Store Logo */}
                            {appUrls.android ? (
                                <a href={appUrls.android} target="_blank" rel="noopener noreferrer">
                                    <img src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" alt="Get it on Google Play" className="h-12 w-auto" />
                                </a>
                            ) : (
                                <img src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" alt="Get it on Google Play" className="h-12 w-auto opacity-50 cursor-not-allowed" style={{ pointerEvents: 'none' }} />
                            )}
                            {/* App Store Logo */}
                            {appUrls.ios ? (
                                <a href={appUrls.ios} target="_blank" rel="noopener noreferrer">
                                    <img src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg" alt="Download on the App Store" className="h-12 w-auto" />
                                </a>
                            ) : (
                                <img src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg" alt="Download on the App Store" className="h-12 w-auto opacity-50 cursor-not-allowed" style={{ pointerEvents: 'none' }} />
                            )}
                            <Link to="/signup" className="px-6 py-3 bg-gray-900 text-white rounded font-bold hover:bg-gray-800 transition-colors text-lg">Sign up for web access</Link>
                        </div>
                    </div>
                    <div className="flex-1 flex justify-center">
                        <img src="/images/logo1.png" alt="App Mockup" className="rounded-xl shadow-2xl w-64 h-64 object-contain bg-gray-800" />
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="py-16 bg-gray-900">
                <div className="container mx-auto px-4">
                    <h2 className="text-3xl font-bold text-center mb-4 text-white">Frequently Asked Questions</h2>
                    <p className="text-center text-gray-300 mb-12 max-w-2xl mx-auto">
                        Everything you need to know about Fixisy
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                        {faqs.map((faq, i) => (
                            <div key={i} className="bg-gray-800 rounded-lg p-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
                                <h3 className="text-lg font-semibold text-cyan-400 mb-2">{faq.q}</h3>
                                <p className="text-gray-300">{faq.a}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-8 bg-gray-800 mt-auto">
                <div className="container mx-auto px-4 flex flex-col sm:flex-row justify-between items-center gap-4 sm:gap-0">
                    <div className="text-gray-400 mb-2 sm:mb-0 text-center sm:text-left">
                        © 2025 Kifayti Retail Private Limited. All rights reserved.
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-6 items-center">
                        <Link to="/terms" className="text-gray-400 hover:text-cyan-400 transition-colors">
                            Terms & Conditions
                        </Link>
                        <Link to="/privacy" className="text-gray-400 hover:text-cyan-400 transition-colors">
                            Privacy Policy
                        </Link>
                        <Link to="/support" className="text-gray-400 hover:text-cyan-400 transition-colors">
                            Support
                        </Link>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default LandingPage; 